import React, { useContext, useEffect, useMemo } from 'react';
import { produce } from 'immer';
import { type Expression } from 'jsep';
import { type AnyConditions } from 'json-rules-engine';
import _ from 'lodash';
import {
  convertAstToJsonRulesEngine,
  convertJsonRulesEngineToAst,
  validateAst,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-context';
import { DisableContainer } from '@bbkAdminComponents/play/disable-container';
import {
  PlayContext,
  PlayObjectProperty,
  PlayValidationErrorType,
} from '@bbkAdminComponents/play/play-context';
import { TriggerEventType } from '@bbkAdminComponents/play/play-types';
import { CollapsableForm } from '@bbkAdminComponents/play/play-utils';
import {
  GenericSelect,
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import { BrightbackExpression } from '@bbkAdminUtils/brightback-expression';
import { cs } from '@bbkAdminUtils/css-utils';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { Icons } from '@bbkAdminUtils/icons';

// Re-export for backward compatibility
export { TriggerEventType } from '@bbkAdminComponents/play/play-types';

import '@bbkAdminComponents/play/plays.scss';

enum EventType {
  WORKING = 'working',
  NONWORKING = 'nonWorking',
  HEADING = 'heading',
}

export const PlayTrigger: React.FC = () => {
  const {
    getPlayObjectProperty,
    setPlayObjectProperty,
    playValidationErrors,
    setPlayValidationErrors,
  } = useContext(PlayContext);
  const playTrigger = getPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER);

  const playTriggerTypeValue = useMemo(
    () => playTrigger?.event_type,
    [playTrigger]
  );

  const triggerData = useMemo(() => {
    if (playTriggerTypeValue !== TriggerEventType.PAGE_VISIT) return [];
    if (playTrigger && _.isEmpty(playTrigger)) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
        event_type: TriggerEventType.PAGE_VISIT,
        expression: "Boolean.or(value('page_url') == '')",
      });
      return [];
    }
    const ast = BrightbackExpression.stringToAst(
      playTrigger?.expression as string
    );
    const validatedAst = validateAst(ast);
    const jsonRule = convertAstToJsonRulesEngine(validatedAst) as AnyConditions;
    return jsonRule.any as { fact: string; value: string; operator: string }[];
  }, [playTrigger, playTriggerTypeValue]);

  const isHostedPagesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_LIFECYCLE_HOSTED_PAGES
  );

  const triggerOptionsData: SelectOption<TriggerEventType>[] = [
    { value: TriggerEventType.PAGE_VISIT, label: 'On page visit' },
    { value: TriggerEventType.ANY_PAGE_VISIT, label: 'Any page visit' },
  ];

  const triggerOptions = useMemo(() => {
    if (isHostedPagesEnabled) {
      return [
        ...triggerOptionsData,
        { value: TriggerEventType.ON_CLICK, label: 'On click' },
      ];
    }

    return triggerOptionsData;
  }, [isHostedPagesEnabled]);

  enum FieldType {
    fact = 'fact',
    operator = 'operator',
    value = 'value',
  }

  const convertJsonRuleToString = (json: any) => {
    const ast = convertJsonRulesEngineToAst(json);
    return BrightbackExpression.astToString(ast as Expression);
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleEventChange = (event: TriggerEventType) => {
    if (event === TriggerEventType.ANY_PAGE_VISIT) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
        event_type: TriggerEventType.ANY_PAGE_VISIT,
        expression: 'Boolean.or()',
      });
    } else if (event === TriggerEventType.PAGE_VISIT) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
        event_type: TriggerEventType.PAGE_VISIT,
        expression: "Boolean.or(value('page_url') == '')",
      });
    } else if (event === TriggerEventType.ON_CLICK) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
        event_type: TriggerEventType.ON_CLICK,
        expression: 'Boolean.or()',
      });
    }
  };

  const handleFieldChange = (
    field: FieldType,
    value: string,
    index: number
  ) => {
    if (FieldType.operator === field) {
      const updatedData = produce(triggerData, (draft) => {
        _.set(draft, [index, 'operator'], value);
      });
      const res = convertJsonRuleToString({ any: updatedData });
      setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
        event_type: TriggerEventType.PAGE_VISIT,
        expression: res,
      });
    } else if (FieldType.value === field) {
      const updatedData = produce(triggerData, (draft) => {
        _.set(draft, [index, 'value'], value);
        if (_.get(draft, [index, 'operator']) === 'Generic.isBlank') {
          _.set(draft, [index, 'operator'], '==');
        }
      });

      const stringRule = convertJsonRuleToString({ any: updatedData });
      setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
        event_type: TriggerEventType.PAGE_VISIT,
        expression: stringRule,
      });
    }
  };

  const validationErrorPath = [PlayValidationErrorType.Trigger, 'error'];
  const triggerValidationErrors = _.get(
    playValidationErrors,
    validationErrorPath
  );
  const showValidationError =
    triggerValidationErrors && triggerValidationErrors?.show;

  useEffect(() => {
    const validationError: (string | undefined)[] = [];
    let hasError = false;
    triggerData.forEach((data, index) => {
      if (
        ['==', '!=', 'Generic.isBlank'].includes(data.operator) &&
        (_.isEmpty(data.value) || !validateUrl(data.value))
      ) {
        validationError[index] = 'Value must be a valid URL';
        hasError = true;
      } else {
        validationError[index] = undefined;
      }
    });
    setPlayValidationErrors((prev) =>
      produce(prev, (draft) => {
        _.set(draft, validationErrorPath, {
          type: TriggerEventType.PAGE_VISIT,
          triggerErrors: validationError,
          show: false,
          hasError,
        });
      })
    );
  }, [triggerData]);

  const getTriggerProperties = (
    trigger: TriggerEventType
  ): SelectOption<string>[] => {
    switch (trigger) {
      case TriggerEventType.PAGE_VISIT: {
        return [
          {
            label: 'Page URL',
            value: 'page_url',
          },
        ];
      }
      default: {
        return [];
      }
    }
  };

  const getTriggerPropertyOperators = (
    trigger: TriggerEventType
  ): SelectOption<string>[] => {
    switch (trigger) {
      case TriggerEventType.PAGE_VISIT: {
        return [
          {
            label: 'Is',
            value: '==',
          },
          {
            label: 'Is not',
            value: '!=',
          },

          {
            label: 'Contains',
            value: 'String.includes',
          },
          {
            label: 'Does not contain',
            value: 'String.notIncludes',
          },
        ];
      }
      default: {
        return [];
      }
    }
  };

  const handleAddRow = () => {
    const updatedData = produce(triggerData, (draft) => {
      draft.push({
        fact: 'page_url',
        operator: '==',
        value: '',
      });
    });
    const res = convertJsonRuleToString({ any: updatedData });
    setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
      event_type: TriggerEventType.PAGE_VISIT,
      expression: res,
    });
  };

  const handleRemoveRow = (index: number) => {
    const updatedTriggerData = produce(triggerData, (draft) => {
      _.pullAt(draft, index);
    });
    const res = convertJsonRuleToString({ any: updatedTriggerData });
    setPlayObjectProperty(PlayObjectProperty.PLAY_TRIGGER, {
      event_type: TriggerEventType.PAGE_VISIT,
      expression: res,
    });
  };

  return (
    <div className="play-trigger tw-flex">
      <div className="v-line-background tw-border-danger-500">
        <div className="tw-mb-8 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#AEE371] tw-bg-[#DCF5BF]">
          {Icons.playCircle}
        </div>
      </div>
      <div className="tw-py-2 tw-pl-5">
        <CollapsableForm
          title="Trigger"
          subtitle="What activates the play? "
          id={2}
          collapseElement={<div className="tw-min-w-[45rem]" />}
        >
          <DisableContainer>
            <div className="tw-mb-5 tw-mt-4 tw-flex tw-min-w-[45rem] tw-flex-col tw-rounded-sm tw-border tw-border-[#AEE371] tw-bg-white tw-px-6 tw-py-3">
              <div className="tw-flex tw-items-center">
                <span className="tw-font-semibold">Event:</span>
                <div className="tw-mx-5">
                  <GenericSelect
                    className="tw-min-w-48"
                    options={triggerOptions}
                    isSearchable={false}
                    defaultValue={triggerOptions.find(
                      (option) => option.value === playTriggerTypeValue
                    )}
                    onChange={(e) => handleEventChange(e.value)}
                  />
                </div>
              </div>
              {playTriggerTypeValue === TriggerEventType.PAGE_VISIT && (
                <>
                  <div className="h-line tw-my-5 tw-px-10" />
                  <div className="tw-flex tw-flex-col">
                    <div className="tw-flex">
                      <span className="tw-mb-2 tw-mr-4 tw-w-1/4 tw-font-semibold">
                        Property
                      </span>
                      <span className="tw-mb-2 tw-mr-4 tw-w-1/4 tw-font-semibold">
                        Operator
                      </span>
                      <span className="tw-mb-2 tw-mr-4 tw-w-2/4 tw-font-semibold">
                        Value
                      </span>
                    </div>
                    {triggerData.map((row, index) => (
                      <div className="tw-mb-2 tw-flex" key={index}>
                        <div className="tw-mr-4 tw-w-1/4">
                          <GenericSelect
                            options={getTriggerProperties(
                              TriggerEventType.PAGE_VISIT
                            )}
                            isSearchable={false}
                            defaultValue={
                              getTriggerProperties(
                                TriggerEventType.PAGE_VISIT
                              )[0]
                            }
                          />
                        </div>
                        <div className="tw-mr-4 tw-w-1/4">
                          <GenericSelect
                            options={getTriggerPropertyOperators(
                              TriggerEventType.PAGE_VISIT
                            )}
                            isSearchable={false}
                            defaultValue={
                              getTriggerPropertyOperators(
                                TriggerEventType.PAGE_VISIT
                              )[0]
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                FieldType.operator,
                                e.value,
                                index
                              )
                            }
                            value={getTriggerPropertyOperators(
                              TriggerEventType.PAGE_VISIT
                            ).find(
                              (p) =>
                                p.value === row.operator ||
                                (row.operator === 'Generic.isBlank' &&
                                  p.value === '==')
                            )}
                          />
                        </div>
                        <div className="tw-mr-4 tw-flex tw-w-2/4 tw-flex-col">
                          <div className="tw-flex">
                            <input
                              className={cs('bbk-simple-text-input', {
                                '!tw-border-danger-500':
                                  showValidationError &&
                                  triggerValidationErrors?.triggerErrors?.[
                                    index
                                  ],
                              })}
                              value={row.value}
                              placeholder="Enter page URL..."
                              onChange={(e) =>
                                handleFieldChange(
                                  FieldType.value,
                                  e.currentTarget.value,
                                  index
                                )
                              }
                            />
                            <div className="tw-my-auto tw-ml-4 tw-w-8">
                              {index > 0 && (
                                <button
                                  className="trash-icon"
                                  onClick={() => handleRemoveRow(index)}
                                >
                                  <span>{Icons.trash1}</span>
                                </button>
                              )}
                            </div>
                          </div>
                          <span className="tw-text-xs tw-text-danger-500">
                            {showValidationError &&
                              triggerValidationErrors?.triggerErrors?.[index]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="tw-pt-2">
                    <button
                      onClick={handleAddRow}
                      className="tw-font-semibold tw-text-[#295E7D]"
                    >
                      + Add another rule
                    </button>
                  </div>
                </>
              )}
              {playTriggerTypeValue === TriggerEventType.ON_CLICK && (
                <div className="tw-mt-4 tw-flex tw-flex-row tw-items-center tw-gap-1 tw-text-sm">
                  <span className="tw-font-semibold tw-underline">
                    Important:
                  </span>
                  Only clicks to element with "bb-expand" ID is used to trigger
                  this play.
                  <a
                    target="_blank"
                    className="tw-font-semibold tw-text-cbrprimary-600"
                    href=""
                    rel="noreferrer"
                  >
                    Learn more
                  </a>
                </div>
              )}
            </div>
          </DisableContainer>
        </CollapsableForm>
      </div>
    </div>
  );
};
